import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, fetchSettings, formatApiError } from "@/lib/api";

const GROUPS = [
  ["Buy button", [["buy_url", "Buy Now link (URL)", "https://your-payment-page.com"], ["buy_text", "Button text"], ["price", "Price (number only)"], ["price_label", "Price label"], ["price_note", "Price note"]]],
  ["Other buttons & links", [["demo_text", "Demo button text"], ["demo_url", "Demo button link", "https://trafficexchange.app/"], ["hero_cta_text", "Hero main button text"], ["hero_cta_url", "Hero main button link", "#pricing or https://…"], ["final_cta_text", "Bottom banner button text"], ["final_cta_url", "Bottom banner button link", "#pricing or https://…"]]],
  ["Demo login details (shown under the demo button — leave empty to hide)", [["demo_admin_user", "Demo admin username / email"], ["demo_admin_pass", "Demo admin password"], ["demo_member_user", "Demo member username / email"], ["demo_member_pass", "Demo member password"]]],
  ["Support contact", [["phone", "24/7 phone number", "+1 (555) 000-0000"], ["email", "Support email", "support@yourdomain.com"]]],
  ["Hero section", [["announcement", "Top announcement"], ["hero_line1", "Headline line 1"], ["hero_line2", "Headline line 2 (highlighted)"], ["hero_line3", "Headline line 3"], ["hero_sub", "Sub-headline", "", true]]],
];

const field = "rounded-xl border-slate-300 bg-white text-slate-900 placeholder:text-slate-400";

const BLANK = { name: "", role: "", quote: "", rating: 5, image: "" };

const TestimonialsEditor = ({ list, onChange }) => {
  const update = (i, k, v) => onChange(list.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="testimonials-editor">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-slate-900">Testimonials</h2>
        <button type="button" onClick={() => onChange([...list, BLANK])} data-testid="testimonial-add-button" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-900 hover:border-blue-600 hover:text-blue-600"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <p className="mt-1 text-sm text-slate-600">Remove all testimonials to hide the section.</p>
      <div className="mt-5 space-y-4">
        {list.map((t, i) => (
          <div key={i} className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[1fr_1fr_110px_auto]" data-testid={`testimonial-row-${i}`}>
            <Input required placeholder="Name" value={t.name} onChange={(e) => update(i, "name", e.target.value)} className={`h-11 ${field}`} data-testid={`testimonial-name-${i}`} />
            <Input placeholder="Role / website" value={t.role} onChange={(e) => update(i, "role", e.target.value)} className={`h-11 ${field}`} data-testid={`testimonial-role-${i}`} />
            <Input type="number" min={1} max={5} value={t.rating} onChange={(e) => update(i, "rating", Math.min(5, Math.max(1, Number(e.target.value) || 5)))} className={`h-11 ${field}`} data-testid={`testimonial-rating-${i}`} aria-label="Stars" />
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`testimonial-remove-${i}`} className="flex h-11 items-center justify-center rounded-xl border border-slate-300 px-3 text-slate-600 hover:border-red-400 hover:text-red-600" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
            <Input placeholder="Photo URL (https://… or /img/…)" value={t.image || ""} onChange={(e) => update(i, "image", e.target.value)} className={`h-11 md:col-span-4 ${field}`} data-testid={`testimonial-image-${i}`} />
            <Textarea required rows={2} placeholder="Quote" value={t.quote} onChange={(e) => update(i, "quote", e.target.value)} className={`md:col-span-4 ${field}`} data-testid={`testimonial-quote-${i}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export const ContentEditor = () => {
  const qc = useQueryClient();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchSettings().then(setS);
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Website updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-blue-600" />;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="content-editor-form">
      {GROUPS.map(([title, fields]) => (
        <div key={title} className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
          <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            {fields.map(([k, label, ph, multi]) => (
              <label key={k} className={`block ${multi || k === "buy_url" ? "md:col-span-2" : ""}`}>
                <span className="mb-1.5 block text-sm font-semibold text-slate-700">{label}</span>
                {multi ? (
                  <Textarea rows={3} value={s[k] ?? ""} placeholder={ph} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={field} data-testid={`setting-${k}-input`} />
                ) : (
                  <Input value={s[k] ?? ""} placeholder={ph} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={`h-11 ${field}`} data-testid={`setting-${k}-input`} />
                )}
              </label>
            ))}
          </div>
        </div>
      ))}
      <TestimonialsEditor list={s.testimonials || []} onChange={(t) => setS({ ...s, testimonials: t })} />
      <p className="text-sm text-slate-600">Leave the Buy Now link empty to send visitors to the pricing section. Links starting with # jump to a section (#features, #how, #testimonials, #pricing, #support); full https:// links open in a new tab. Leave phone/email empty to hide them.</p>
      <button disabled={busy} data-testid="settings-save-button" className="font-display inline-flex items-center gap-2 rounded-full bg-[#FACC15] px-7 py-3.5 text-sm font-bold uppercase text-[#070B14] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
      </button>
    </form>
  );
};
